import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  User,
  Trash2,
  Shield,
  UserCircle,
  Pencil,
  TrashIcon,
} from "lucide-react";
import useAuthStore from "@/stores/user/authStore";
import userService from "@/services/userService";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { CancelButton, ConfirmButton } from "@/components/common/buttons";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SettingsTab = "general" | "account" | "privacy" | "danger";

interface SettingsNavItem {
  id: SettingsTab;
  label: string;
  icon: React.ReactNode;
  description: string;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, setUser, profile_image } = useAuthStore();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const navigate = useNavigate();

  // Form states
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [username, setUsername] = useState(user?.username || "");
  const [isPrivate, setIsPrivate] = useState(user?.is_private || false);
  // Track if there are any changes
  const hasChanges = React.useMemo(() => {
    if (!user) return false;
    return (
      username !== user.username ||
      isPrivate !== user.is_private ||
      firstName !== user?.first_name ||
      lastName !== user?.last_name
    );
  }, [user, username, isPrivate, firstName, lastName]);

  const settingsNavItems: SettingsNavItem[] = [
    {
      id: "general",
      label: "General Info",
      icon: <UserCircle className="w-4 h-4" strokeWidth={1.5} />,
      description: "View your basic information",
    },
    {
      id: "account",
      label: "Account",
      icon: <User className="w-4 h-4" strokeWidth={1.5} />,
      description: "Manage your username",
    },
    {
      id: "privacy",
      label: "Privacy",
      icon: <Shield className="w-4 h-4" strokeWidth={1.5} />,
      description: "Control your privacy settings",
    },
    {
      id: "danger",
      label: "Danger Zone",
      icon: <Trash2 className="w-4 h-4" strokeWidth={1.5} />,
      description: "Delete your account",
    },
  ];

  const handleSave = async () => {
    if (!user) return;
    setIsLoading(true);

    try {
      const response = await userService.updateProfile({
        username,
        first_name: firstName,
        last_name: lastName,
        name: `${firstName} ${lastName}`,
        is_private: isPrivate,
      });

      if (response.success) {
        setUser({
          ...user,
          username,
          is_private: isPrivate,
          first_name: firstName,
          last_name: lastName,
          name: `${firstName} ${lastName}`,
        });

        toast({
          title: "Profile Updated",
          description: "Your profile has been updated successfully.",
        });

        onClose();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setIsDeleting(true);

    try {
      await userService.deleteAccount();
      navigate("/logout");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return (
          <div className="space-y-6">
            <div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label
                    htmlFor="firstName"
                    className="text-sm font-medium text-neutral-700"
                  >
                    First Name
                  </Label>
                  <div className="relative">
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Enter your first name"
                      className="h-9 rounded-lg text-sm font-light text-neutral-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Pencil
                      className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                      strokeWidth={1.5}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="lastName"
                    className="text-sm font-medium text-neutral-700"
                  >
                    Last Name
                  </Label>
                  <div className="relative">
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Enter your last name"
                      className="h-9 rounded-lg text-sm font-light text-neutral-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Pencil
                      className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                      strokeWidth={1.5}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-neutral-700">Email</h4>
              <p className="text-sm font-light text-neutral-600">
                {user?.email}
              </p>
            </div>
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-neutral-700">
                Profile Image
              </h4>
              <div className="h-16 w-16 rounded-full overflow-hidden bg-neutral-100">
                <AvatarImage
                  src={profile_image || ""}
                  alt="Profile"
                  firstName={user?.first_name || ""}
                  lastName={user?.last_name || ""}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        );

      case "account":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="username"
                className="text-sm font-medium text-neutral-900"
              >
                Username
              </Label>
              <div className="relative">
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="h-9 rounded-lg text-sm font-light text-neutral-600 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <Pencil
                  className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
        );

      case "privacy":
        return (
          <div className="space-y-4">
            <div
              className={cn(
                "flex items-center justify-between rounded-xl p-4 transition-colors",
                isPrivate
                  ? "bg-indigo-50 border border-indigo-100"
                  : "bg-neutral-50 border border-neutral-100"
              )}
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-neutral-800">
                    Private Account
                  </h4>
                  <span
                    className={cn(
                      "px-2 py-0.5 rounded-full text-xs font-medium",
                      isPrivate
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-neutral-100 text-neutral-600"
                    )}
                  >
                    {isPrivate ? "Private" : "Public"}
                  </span>
                </div>
                <div className="space-y-3">
                  <p className="text-xs font-light text-neutral-600">
                    {isPrivate
                      ? "Your profile is private. Only approved followers can see your content."
                      : "Your profile is public. Anyone can see your content."}
                  </p>
                </div>
              </div>
              <Switch
                checked={isPrivate}
                onCheckedChange={setIsPrivate}
                aria-label="Toggle private account"
                className={cn(
                  "transition-colors",
                  isPrivate
                    ? "data-[state=checked]:bg-indigo-600"
                    : "data-[state=unchecked]:bg-neutral-200"
                )}
              />
            </div>
          </div>
        );

      case "danger":
        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-red-100 p-4 bg-red-50">
              <div className="space-y-4">
                <p className="text-xs font-light text-red-600">
                  Deleting your account is permanent. All your data will be
                  permanently removed and cannot be recovered.
                </p>
                <Button
                  variant="destructive"
                  className="w-full h-10 font-light text-white bg-rose-500 hover:bg-rose-600 text-sm rounded-lg flex items-center justify-center gap-2"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[800px] max-h-[85vh] overflow-hidden rounded-2xl p-0">
          <div className="flex h-[500px]">
            {/* Sidebar */}
            <div className="w-[240px] border-r border-neutral-100 p-4 flex flex-col gap-1">
              {settingsNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    "flex items-center gap-2 w-full p-2 rounded-lg text-sm transition-colors",
                    activeTab === item.id
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                  )}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col">
              <DialogHeader className="p-4 border-b border-neutral-100">
                <DialogTitle className="text-lg font-medium text-neutral-900">
                  {
                    settingsNavItems.find((item) => item.id === activeTab)
                      ?.label
                  }
                </DialogTitle>
                <DialogDescription className="text-sm font-light text-neutral-600">
                  {
                    settingsNavItems.find((item) => item.id === activeTab)
                      ?.description
                  }
                </DialogDescription>
              </DialogHeader>

              <div className="p-4 flex-1 overflow-y-auto">
                {renderTabContent()}
              </div>

              <DialogFooter className="p-4 border-t border-neutral-100">
                <CancelButton onClick={onClose} disabled={isLoading} />
                <ConfirmButton
                  title="Save"
                  onClick={handleSave}
                  isLoading={isLoading}
                  disabled={isLoading || !hasChanges}
                />
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-2xl sm:max-w-[500px]">
          <DialogHeader className="flex flex-row items-start space-x-4 space-y-0">
            <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
              <TrashIcon className="w-5 h-5 text-rose-500" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold text-neutral-800 mb-2">
                Delete Account
              </DialogTitle>
              <DialogDescription className="text-neutral-600 text-sm mb-4 font-light">
                Are you sure you want to delete your account?
              </DialogDescription>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-3 sm:gap-1">
            <CancelButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            />
            <ConfirmButton
              title="Confirm"
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              isLoading={isDeleting}
              loadingText="Deleting..."
              type="destructive"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
